/*
 * SendLeadsSummaryDialog — email a summary of ALL leads for a chosen period.
 *
 * Includes converted and killed leads so leadership sees the full flow
 * (not just the still-active queue). Sender is fixed on the backend to
 * salesadmin@xentra.co.uk (via Microsoft Graph). Mirrors the UX of
 * Projects → Send Project Update: recipient chips, preview table, one-click send.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Send, X } from "lucide-react";
import { toast } from "sonner";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PERIOD_OPTIONS = [
  { v: "current_month", l: "This month" },
  { v: "current_q",     l: "This quarter" },
  { v: "last_month",    l: "Last month" },
  { v: "last_q",        l: "Last quarter" },
  { v: "custom",        l: "Custom range…" },
];

const STATUS_BADGE = {
  Unqualified: "bg-slate-100 text-slate-700",
  MQL:         "bg-blue-100 text-blue-800",
  SQL:         "bg-violet-100 text-violet-800",
  Converted:   "bg-emerald-100 text-emerald-800",
  Killed:      "bg-red-100 text-red-800",
};

// Collapse `status` + `killed_at` into one display label — matches the
// backend's `_summary_lead_status` so the preview shows exactly what the
// recipient will see in the email body.
const displayStatus = (l) => {
  if (l?.killed_at) return "Killed";
  return l?.status || "Unqualified";
};

export default function SendLeadsSummaryDialog() {
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState("current_month");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [recipients, setRecipients] = useState(["salesadmin@xentra.co.uk"]);
  const [entry, setEntry] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const body = { recipients: ["salesadmin@xentra.co.uk"], period };
      if (period === "custom") {
        if (!startDate || !endDate) { setPreview(null); setLoading(false); return; }
        body.start_date = startDate;
        body.end_date = endDate;
      }
      const r = await api.post("/leads/summary-preview", body);
      setPreview(r.data);
    } catch (e) {
      setPreview(null);
      if (period !== "custom") toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  // Refresh preview when open, period or custom dates change.
  useEffect(() => {
    if (!open) return;
    if (period === "custom" && (!startDate || !endDate)) return;
    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, period, startDate, endDate]);

  const addEntry = () => {
    const parts = entry.split(/[\n,;\s]+/).map((s) => s.trim()).filter(Boolean);
    const next = [...recipients];
    for (const e of parts) {
      if (!EMAIL_RE.test(e)) { toast.error(`Not a valid email: ${e}`); continue; }
      if (!next.some((x) => x.toLowerCase() === e.toLowerCase())) next.push(e);
    }
    setRecipients(next);
    setEntry("");
  };

  const removeRecipient = (e) => setRecipients(recipients.filter((x) => x !== e));

  const send = async () => {
    let recips = [...recipients];
    if (entry.trim()) {
      const e = entry.trim();
      if (EMAIL_RE.test(e) && !recips.some((x) => x.toLowerCase() === e.toLowerCase())) recips.push(e);
    }
    if (recips.length === 0) return toast.error("Add at least one recipient");
    if (period === "custom" && (!startDate || !endDate)) return toast.error("Pick a start and end date");

    setSending(true);
    try {
      const body = { recipients: recips, period };
      if (period === "custom") { body.start_date = startDate; body.end_date = endDate; }
      const r = await api.post("/leads/send-summary", body);
      toast.success(`Sent ${r.data.count} lead${r.data.count === 1 ? "" : "s"} to ${r.data.sent_to.length} recipient${r.data.sent_to.length === 1 ? "" : "s"}`);
      setOpen(false);
      setEntry("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send summary");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2 border-slate-300" data-testid="send-leads-summary-btn">
          <Mail className="h-4 w-4" /> Send Summary
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl" data-testid="send-leads-summary-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="h-4 w-4 text-blue-600" /> Send leads summary</DialogTitle>
          <DialogDescription>
            Emails every lead created in the selected period — including converted and killed — grouped by owner. Sent from{" "}
            <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">salesadmin@xentra.co.uk</span>{" "}
            via Microsoft 365.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Period</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="leads-summary-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((p) => (
                  <SelectItem key={p.v} value={p.v} data-testid={`leads-summary-period-opt-${p.v}`}>{p.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {period === "custom" && (
            <>
              <div>
                <Label className="text-xs">Start date</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="leads-summary-start-date" />
              </div>
              <div>
                <Label className="text-xs">End date</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="leads-summary-end-date" />
              </div>
            </>
          )}
        </div>

        <div>
          <Label className="text-xs">Recipients</Label>
          <div className="mt-1 flex flex-wrap gap-1.5 rounded-md border border-slate-200 p-2 min-h-[42px]" data-testid="leads-summary-recipients">
            {recipients.map((e) => (
              <span key={e} className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 text-xs px-2 py-0.5 border border-blue-200">
                {e}
                <button type="button" onClick={() => removeRecipient(e)} data-testid={`remove-recipient-${e}`} className="hover:text-blue-900">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            <input
              value={entry}
              onChange={(e) => setEntry(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addEntry(); } }}
              onBlur={() => { if (entry.trim()) addEntry(); }}
              placeholder="Add email + Enter"
              className="flex-1 min-w-[160px] text-sm outline-none bg-transparent"
              data-testid="leads-summary-recipient-input"
            />
          </div>
          <p className="mt-1 text-[11px] text-slate-400">Defaults to salesadmin@xentra.co.uk. Add or remove addresses as needed.</p>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-mono">Preview</div>
            <div className="text-xs text-slate-500" data-testid="leads-summary-preview-header">
              {loading ? "Loading…" : preview
                ? `${preview.count} lead${preview.count === 1 ? "" : "s"} · ${preview.period_label}`
                : (period === "custom" ? "Pick a start and end date to see the preview" : "—")}
            </div>
          </div>
          <div className="max-h-[240px] overflow-auto rounded-md border border-slate-200">
            {loading ? (
              <div className="p-6 text-center text-sm text-slate-500">Loading…</div>
            ) : preview && preview.leads && preview.leads.length > 0 ? (
              <table className="w-full text-[12px]">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Company</th>
                    <th className="px-2 py-1.5 text-left">Contact</th>
                    <th className="px-2 py-1.5 text-left">Status</th>
                    <th className="px-2 py-1.5 text-left">Owner</th>
                    <th className="px-2 py-1.5 text-left">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.leads.map((l) => {
                    const st = displayStatus(l);
                    return (
                      <tr key={l.id} className="border-t border-slate-100" data-testid={`leads-summary-row-${l.id}`}>
                        <td className="px-2 py-1.5 font-medium text-slate-800">{l.company || "—"}</td>
                        <td className="px-2 py-1.5 text-slate-600">
                          {[l.first_name, l.last_name].filter(Boolean).join(" ") || "—"}
                        </td>
                        <td className="px-2 py-1.5">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${STATUS_BADGE[st] || "bg-slate-100 text-slate-700"}`}>{st}</span>
                        </td>
                        <td className="px-2 py-1.5 text-slate-600">{l.owner_name || <span className="text-slate-400">Unassigned</span>}</td>
                        <td className="px-2 py-1.5 text-slate-500 font-mono">{(l.created_at || "").slice(0, 10)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="p-6 text-center text-sm text-slate-500" data-testid="leads-summary-empty">
                {preview ? "No leads in this period." : "—"}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="leads-summary-cancel">Cancel</Button>
          <Button
            onClick={send}
            disabled={sending || loading || !preview || preview.count === 0}
            className="gap-2 bg-blue-600 hover:bg-blue-700"
            data-testid="leads-summary-send-confirm"
          >
            <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send summary"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
